import React, { ReactChild, ReactElement, useEffect } from "react";
import CancelIcon from "@mui/icons-material/Close";
import { Fade, IconButton } from "@mui/material";

import { DialogAction } from "../../models/Enums";

interface IModal {
  /** Parent modal wrapper className */
  modalClassName?: string;

  /** Inline style applied to the dialog box - lets a caller size the dialog precisely
   * (e.g. to match another element's rendered dimensions) instead of only the preset
   * app-modal-xs/sm/md/lg percentage classes. */
  dialogStyle?: React.CSSProperties;

  /** Inline style applied to the body (content) wrapper - lets a caller override the
   * default .app-modal-body padding without affecting every other Modal usage. */
  bodyStyle?: React.CSSProperties;

  /** Dialog header */
  title?: string;

  /** An another component  */
  children: ReactChild;
  /** used to open/close the dialog */
  open: boolean;

  /** close dialog on esc key*/
  onEscPress: boolean;

  // events
  /** Triggers when user clicks close button */
  onDialogClose?: (action?: DialogAction) => void;
}

/** Renders the MalariaDialog  */
function Modal(props: IModal) {
  const {
    children,
    modalClassName,
    dialogStyle,
    bodyStyle,
    title,
    onDialogClose,
    open,
    onEscPress,
  } = props;

  useEffect(() => {
    if (open) {
      document.body.classList.add("app-modal-open");
    } else {
      document.body.classList.remove("app-modal-open");
    }
  }, [open]);

  // Renders the Cancel Icon
  const Cancel = (): ReactElement =>
    onDialogClose ? (
      <IconButton
        onClick={() => onDialogClose && onDialogClose(DialogAction.Close)}
        title="Close"
        aria-label="Close dialog"
        className="close-modal"
      >
        <CancelIcon />
      </IconButton>
    ) : (
      <></>
    );

  return open ? (
    <Fade in={open} timeout={{ enter: 300, exit: 300 }}>
      <div
        id="modal-wrapper"
        className={`app-modal app-modal-show  ${modalClassName || "app-modal-sm"}`}
        onKeyUp={(e: React.KeyboardEvent) => {
          if (onEscPress) {
            // close dialog on esc key
            if (e.keyCode === 27) {
              onDialogClose && onDialogClose(DialogAction.Close);
            }
          }
        }}
      >
        <div className={`app-modal-dialog `} style={dialogStyle} role="document">
          <div className="app-modal-content">
            <div className="app-modal-header d-flex">
              <h5 className="app-modal-header-text">{title || ""}</h5>
              <div className="ml-auto">
                <Cancel />
              </div>
            </div>

            <div className="app-modal-body" style={bodyStyle}>{children}</div>
          </div>
        </div>
      </div>
    </Fade>
  ) : (
    <></>
  );
}

export default Modal;
